import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Coins, Target, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { catMeta } from "../lib/verdictly";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

export default function ReviewQueue() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState(null);
  const [needsProfile, setNeedsProfile] = useState(false);
  const [active, setActive] = useState(null);   // task being reviewed
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/reviewer/tasks");
      setTasks(r.data);
    } catch (e) {
      if (e.response?.status === 428) setNeedsProfile(true);
      else toast.error("Could not load tasks");
      setTasks([]);
    }
  };
  useEffect(() => { load(); }, []);

  const openTask = (t) => { setActive(t); setAnswers({}); };

  const submit = async () => {
    if (!active) return;
    for (const q of active.questions) {
      if (q.required && !answers[q.id]) return toast.error(`Answer: ${q.label}`);
    }
    setSubmitting(true);
    try {
      const r = (await api.post("/reviews", { task_id: active.id, answers })).data;
      toast.success(`Review accepted — +${active.symbol}${r.payout} earned! 💸`);
      setActive(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    finally { setSubmitting(false); }
  };

  if (needsProfile) {
    return (
      <div className="max-w-xl" data-testid="review-needs-profile">
        <h1 className="font-heading font-bold uppercase tracking-tighter text-4xl">Start earning</h1>
        <p className="text-zinc-400 mt-2 mb-6">Set up your reviewer profile first — it takes 20 seconds and unlocks matched, paid tasks.</p>
        <button data-testid="go-profile-btn" onClick={() => navigate("/app/reviewer-profile")}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand hover:bg-brand-hover text-black font-bold transition-colors">
          Set up profile <ArrowRight size={16} />
        </button>
      </div>
    );
  }

  return (
    <div data-testid="review-queue">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold uppercase tracking-tighter text-4xl sm:text-5xl">Review & earn</h1>
          <p className="text-zinc-400 mt-2">Give an honest verdict. Get paid per accepted review.</p>
        </div>
        <button data-testid="edit-profile-link" onClick={() => navigate("/app/reviewer-profile")} className="text-sm text-zinc-400 hover:text-brand transition-colors">Edit profile</button>
      </div>

      {tasks === null ? (
        <div className="text-zinc-400">Loading…</div>
      ) : tasks.length === 0 ? (
        <div data-testid="review-empty" className="rounded-xl border border-white/10 bg-zinc-900/50 p-10 text-center text-zinc-400">
          No matched tasks right now. Check back soon — new verdicts drop constantly.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {tasks.map((t) => {
            const m = catMeta(t.category); const Icon = m.icon;
            return (
              <div key={t.id} data-testid={`review-card-${t.id}`} className="rounded-xl border border-white/10 bg-zinc-900/60 p-5 flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase px-2.5 py-1 rounded-full" style={{ color: m.accent, background: m.soft }}>
                    <Icon size={13} /> {t.category_name}
                  </span>
                  {t.targeted && <span className="inline-flex items-center gap-1 text-[10px] text-brand-accent tracking-widest uppercase"><Target size={11} /> Matched</span>}
                </div>
                {t.content_type === "text"
                  ? <p className="text-sm text-zinc-200 line-clamp-4 flex-1">{t.content}</p>
                  : <img src={t.content} alt="" className="rounded-lg max-h-40 w-full object-cover flex-1" />}
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/10">
                  <span className="inline-flex items-center gap-1.5 font-bold" style={{ color: m.accent }}><Coins size={15} /> {t.symbol}{t.payout}</span>
                  <span className="text-xs text-zinc-500">{t.slots_left} of {t.reviewers_required} slots left</span>
                </div>
                <button data-testid={`review-btn-${t.id}`} onClick={() => openTask(t)}
                  className="mt-3 w-full py-2.5 rounded-full font-bold text-black transition-transform hover:-translate-y-0.5" style={{ background: m.accent }}>
                  Give your verdict
                </button>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => { if (!o) setActive(null); }}>
        <DialogContent data-testid="review-dialog" className="bg-zinc-950 border-white/10 max-h-[90vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading uppercase tracking-tight text-2xl">{active.category_emoji} Your verdict</DialogTitle>
                <DialogDescription>Be honest and specific. Low-effort reviews aren't accepted or paid.</DialogDescription>
              </DialogHeader>
              <div className="rounded-lg border border-white/10 bg-zinc-900/60 p-3 text-sm text-zinc-300">
                {active.content_type === "text" ? active.content : <img src={active.content} alt="" className="rounded max-h-48" />}
              </div>
              <div className="space-y-4">
                {active.questions.map((q) => (
                  <div key={q.id}>
                    <label className="text-sm text-zinc-300 font-medium">{q.label}{q.required && <span className="text-brand"> *</span>}</label>
                    {q.type === "scale" && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {Array.from({ length: q.max - q.min + 1 }).map((_, i) => {
                          const v = q.min + i; const sel = String(answers[q.id]) === String(v);
                          return (
                            <button key={v} data-testid={`ans-${q.id}-${v}`} onClick={() => setAnswers((a) => ({ ...a, [q.id]: v }))}
                              className={`w-9 h-9 rounded-lg text-sm font-bold border transition-colors ${sel ? "bg-brand text-black border-brand" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>{v}</button>
                          );
                        })}
                      </div>
                    )}
                    {q.type === "choice" && (
                      <div className="flex gap-2 mt-2">
                        {q.options.map((opt) => (
                          <button key={opt} data-testid={`ans-${q.id}-${opt}`} onClick={() => setAnswers((a) => ({ ...a, [q.id]: opt }))}
                            className={`px-4 py-2 rounded-full text-sm border transition-colors ${answers[q.id] === opt ? "border-brand bg-brand/10 text-white" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
                            {q.option_labels?.[opt] || opt}
                          </button>
                        ))}
                      </div>
                    )}
                    {q.type === "text" && (
                      <textarea data-testid={`ans-${q.id}`} rows={3} maxLength={500} value={answers[q.id] || ""} placeholder={q.placeholder}
                        onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                        className="mt-2 w-full bg-zinc-950 border border-zinc-800 rounded-sm p-2.5 text-sm focus:border-brand focus:outline-none resize-none" />
                    )}
                  </div>
                ))}
              </div>
              <button data-testid="submit-review-btn" onClick={submit} disabled={submitting}
                className="w-full py-3 rounded-full bg-brand hover:bg-brand-hover text-black font-bold transition-colors disabled:opacity-50">
                {submitting ? "Submitting…" : `Submit & earn ${active.symbol}${active.payout}`}
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
